/**
 * SmartGrid System Map — live 3D visualizer of the whole architecture.
 *
 * Renders the `/api/smartgrid/system-map` graph on a raw <canvas> with a
 * hand-rolled perspective projection (no three.js — zero new deps):
 *   - the five architecture layers are stacked rings (L0 Market Data at the
 *     bottom → L4 Execution on top), each subsystem a glowing node whose
 *     color IS its live health;
 *   - every interaction is an animated edge: pulses travel in the direction
 *     of flow. Green "+" edges are POSITIVE (reinforcing) feedback loops —
 *     learning that sizes up what worked; amber "−" edges are NEGATIVE
 *     (stabilizing) feedback — guards, breakers, throttles that damp
 *     exposure; red dashed edges are BROKEN (an endpoint is down); dotted
 *     gray edges are masked/disabled.
 *   - drag to orbit, wheel to zoom, click a node for its live detail and
 *     every loop it participates in. Auto-rotates while idle.
 *
 * Health comes from the same supervisor views the dashboards read; the page
 * polls every 4s and animates at 60fps between polls.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Orbit, Pause, Play, RefreshCw } from "lucide-react";
import { fetchSgSystemMap } from "@/lib/smartgrid_api";

const C = {
    bg: "#0B0F14", panel: "#0F1620", panel2: "#121C28", border: "#1B2A3A",
    text: "#E6EDF6", sub: "#A7B4C6", dim: "#6F7D92",
    bull: "#2BE38B", bear: "#FF5C6C", cyan: "#2EE6D6", blue: "#3DA7FF",
    warn: "#FFB020", purple: "#9C7BFF",
};

const NODE_COLORS = {
    ok: C.bull, warming: C.warn, degraded: C.warn, down: C.bear,
    off: C.dim, firing: C.bear, blocked: C.warn, shadow: C.purple,
    idle: "#4E6E8A",
};
const NODE_LABELS = {
    ok: "healthy", warming: "warming up", degraded: "degraded",
    down: "broken / down", off: "disabled", firing: "actuating now",
    blocked: "gated", shadow: "shadow", idle: "idle",
};
const EDGE_STYLE = {
    // kind/status → {color, dash, pulse}
    data: { color: C.blue, dash: [], pulse: true },
    positive: { color: C.bull, dash: [], pulse: true, sign: "+" },
    negative: { color: C.warn, dash: [], pulse: true, sign: "−" },
};

const LAYER_GAP = 120;          // world units between layer rings
const LAYER_RADIUS_MIN = 150;
const PERSPECTIVE = 1400;

function layerY(layer) {
    return (layer - 2) * LAYER_GAP;   // L0 bottom (−240) … L4 top (+240)
}

/** Deterministic node positions: each layer is a ring, nodes spaced evenly,
 *  rings staggered so nodes don't stack vertically. */
function layoutNodes(nodes) {
    const byLayer = new Map();
    for (const n of nodes) {
        if (!byLayer.has(n.layer)) byLayer.set(n.layer, []);
        byLayer.get(n.layer).push(n);
    }
    const pos = {};
    for (const [layer, arr] of byLayer.entries()) {
        const r = Math.max(LAYER_RADIUS_MIN, (arr.length * 46) / (2 * Math.PI));
        arr.forEach((n, i) => {
            const a = (i / arr.length) * Math.PI * 2 + layer * 0.55;
            pos[n.id] = {
                x: r * Math.cos(a), y: layerY(layer), z: r * Math.sin(a),
                r,
            };
        });
    }
    return pos;
}

function project(p, yaw, pitch, zoom, cx, cy) {
    const cy1 = Math.cos(yaw), sy1 = Math.sin(yaw);
    const x1 = p.x * cy1 - p.z * sy1;
    const z1 = p.x * sy1 + p.z * cy1;
    const cp = Math.cos(pitch), sp = Math.sin(pitch);
    const y1 = p.y * cp - z1 * sp;
    const z2 = p.y * sp + z1 * cp;
    const s = PERSPECTIVE / (PERSPECTIVE + z2);
    return { x: cx + x1 * s * zoom, y: cy - y1 * s * zoom, s, depth: z2 };
}

function StatusChip({ color, label, count }) {
    return (
        <span className="inline-flex items-center gap-1.5 px-2 h-6 rounded-md text-[11px]"
              style={{ border: `1px solid ${C.border}`, color: C.sub }}>
            <span className="w-2 h-2 rounded-full" style={{ background: color }} />
            {label}{count != null ? ` · ${count}` : ""}
        </span>
    );
}

export default function SmartGridSystemMapPage({ onBack }) {
    const canvasRef = useRef(null);
    const wrapRef = useRef(null);
    const graphRef = useRef(null);          // latest /system-map payload
    const posRef = useRef({});              // node id → world position
    const camRef = useRef({ yaw: 0.7, pitch: 0.42, zoom: 1.0 });
    const dragRef = useRef({ active: false, lastX: 0, lastY: 0, lastMove: 0 });
    const hoverRef = useRef(null);
    const rafRef = useRef(0);
    const spinRef = useRef(true);

    const [graph, setGraph] = useState(null);
    const [selected, setSelected] = useState(null);
    const [err, setErr] = useState("");
    const [spin, setSpin] = useState(true);
    const [kindFilter, setKindFilter] = useState("all"); // all|positive|negative|issues

    spinRef.current = spin;

    const load = useCallback(async () => {
        try {
            const m = await fetchSgSystemMap();
            graphRef.current = m;
            posRef.current = layoutNodes(m.nodes || []);
            setGraph(m);
            setErr("");
        } catch (e) {
            setErr(String(e?.response?.data?.detail || e.message || e));
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 4000);
        return () => clearInterval(t);
    }, [load]);

    // headline counts for the chip row
    const counts = useMemo(() => {
        const g = graph;
        if (!g) return null;
        const edges = g.edges || [];
        return {
            broken: edges.filter((e) => e.status === "broken").length
                + (g.nodes || []).filter((n) => n.status === "down").length,
            firing: (g.nodes || []).filter((n) => n.status === "firing").length,
            positive: edges.filter((e) => e.kind === "positive").length,
            negative: edges.filter((e) => e.kind === "negative").length,
            masked: edges.filter((e) => e.status === "masked").length,
        };
    }, [graph]);

    // ---- render loop ----
    useEffect(() => {
        const canvas = canvasRef.current;
        const wrap = wrapRef.current;
        if (!canvas || !wrap) return undefined;
        const ctx = canvas.getContext("2d");

        const fit = () => {
            const dpr = window.devicePixelRatio || 1;
            const w = wrap.clientWidth, h = wrap.clientHeight;
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(wrap);

        // React's synthetic onWheel is passive (can't preventDefault), so a
        // wheel-zoom would also scroll the page — attach natively instead.
        const wheel = (ev) => {
            ev.preventDefault();
            camRef.current.zoom = Math.max(
                0.45, Math.min(2.6,
                    camRef.current.zoom * (ev.deltaY > 0 ? 0.92 : 1.08)));
            dragRef.current.lastMove = performance.now();
        };
        canvas.addEventListener("wheel", wheel, { passive: false });

        const edgeVisible = (e) => {
            if (kindFilter === "all") return true;
            if (kindFilter === "positive") return e.kind === "positive";
            if (kindFilter === "negative") return e.kind === "negative";
            if (kindFilter === "issues") {
                return ["broken", "masked", "off", "firing"].includes(e.status);
            }
            return true;
        };

        const draw = (tms) => {
            const g = graphRef.current;
            const w = wrap.clientWidth, h = wrap.clientHeight;
            const cx = w / 2, cy = h / 2 + 10;
            ctx.clearRect(0, 0, w, h);
            if (!g) {
                rafRef.current = requestAnimationFrame(draw);
                return;
            }
            const cam = camRef.current;
            if (spinRef.current && !dragRef.current.active
                && tms - dragRef.current.lastMove > 2500) {
                cam.yaw += 0.0022;
            }
            const { yaw, pitch, zoom } = cam;
            const pos = posRef.current;
            const proj = {};
            for (const n of g.nodes) {
                const p = pos[n.id];
                if (p) proj[n.id] = project(p, yaw, pitch, zoom, cx, cy);
            }

            // layer rings (drawn as depth-sorted ellipses)
            const layers = (g.layers || []).slice().sort(
                (a, b) => layerY(b.layer) - layerY(a.layer));
            for (const L of layers) {
                const y = layerY(L.layer);
                const nodesIn = g.nodes.filter((n) => n.layer === L.layer);
                const r = nodesIn.length
                    ? (pos[nodesIn[0].id]?.r || LAYER_RADIUS_MIN) + 34
                    : LAYER_RADIUS_MIN;
                ctx.beginPath();
                let first = true;
                for (let a = 0; a <= Math.PI * 2 + 0.01; a += Math.PI / 36) {
                    const q = project({ x: r * Math.cos(a), y, z: r * Math.sin(a) },
                                      yaw, pitch, zoom, cx, cy);
                    if (first) { ctx.moveTo(q.x, q.y); first = false; }
                    else ctx.lineTo(q.x, q.y);
                }
                ctx.strokeStyle = "rgba(46,230,214,0.10)";
                ctx.lineWidth = 1;
                ctx.stroke();
                // layer label at the ring's leftmost projected point
                const lp = project({ x: -r, y, z: 0 }, yaw, pitch, zoom, cx, cy);
                ctx.font = "10px ui-monospace, monospace";
                ctx.fillStyle = "rgba(111,125,146,0.85)";
                ctx.textAlign = "right";
                ctx.fillText(L.label, lp.x - 8, lp.y + 3);
            }

            // edges, far → near
            const edges = (g.edges || []).filter(edgeVisible)
                .map((e) => {
                    const a = proj[e.from], b = proj[e.to];
                    return a && b ? { e, a, b, depth: (a.depth + b.depth) / 2 } : null;
                })
                .filter(Boolean)
                .sort((p, q) => q.depth - p.depth);
            for (const { e, a, b } of edges) {
                const base = EDGE_STYLE[e.kind] || EDGE_STYLE.data;
                let color = base.color, dash = [], alpha = 0.55, width = 1.1,
                    pulse = base.pulse, pulseHz = 0.00035;
                if (e.status === "broken") {
                    color = C.bear; dash = [6, 5]; alpha = 0.9; width = 1.6;
                    pulse = false;
                } else if (e.status === "masked") {
                    color = C.dim; dash = [2, 5]; alpha = 0.6; pulse = false;
                } else if (e.status === "off") {
                    color = C.dim; dash = [2, 5]; alpha = 0.35; pulse = false;
                } else if (e.status === "firing") {
                    color = C.bear; alpha = 1.0; width = 2.4; pulseHz = 0.0012;
                } else if (e.status === "degraded") {
                    alpha = 0.35;
                }
                const sel = selected
                    && (e.from === selected.id || e.to === selected.id);
                if (selected && !sel) alpha *= 0.18;
                if (sel) { alpha = Math.min(1, alpha + 0.35); width += 0.8; }

                // curved edge: world midpoint lifted outward
                const mid = {
                    x: (posRef.current[e.from].x + posRef.current[e.to].x) / 2,
                    y: (posRef.current[e.from].y + posRef.current[e.to].y) / 2 + 26,
                    z: (posRef.current[e.from].z + posRef.current[e.to].z) / 2,
                };
                const m = project(mid, yaw, pitch, zoom, cx, cy);
                ctx.beginPath();
                ctx.moveTo(a.x, a.y);
                ctx.quadraticCurveTo(m.x, m.y, b.x, b.y);
                ctx.setLineDash(dash);
                ctx.globalAlpha = alpha;
                ctx.strokeStyle = color;
                ctx.lineWidth = width;
                ctx.stroke();
                ctx.setLineDash([]);

                // feedback sign at the curve midpoint
                if (base.sign && e.status !== "off" && (!selected || sel)) {
                    const q = 0.5, iq = 1 - q;
                    const sx = iq * iq * a.x + 2 * iq * q * m.x + q * q * b.x;
                    const sy = iq * iq * a.y + 2 * iq * q * m.y + q * q * b.y;
                    ctx.globalAlpha = Math.min(1, alpha + 0.2);
                    ctx.fillStyle = color;
                    ctx.font = "bold 11px ui-monospace, monospace";
                    ctx.textAlign = "center";
                    ctx.fillText(base.sign, sx, sy - 4);
                }

                // direction pulse
                if (pulse) {
                    const t = ((tms * pulseHz) + (e.from.length * 0.13
                        + e.to.length * 0.07)) % 1;
                    const iq = 1 - t;
                    const px = iq * iq * a.x + 2 * iq * t * m.x + t * t * b.x;
                    const py = iq * iq * a.y + 2 * iq * t * m.y + t * t * b.y;
                    ctx.globalAlpha = Math.min(1, alpha + 0.3);
                    ctx.beginPath();
                    ctx.arc(px, py, e.status === "firing" ? 3.2 : 2.1,
                            0, Math.PI * 2);
                    ctx.fillStyle = color;
                    ctx.fill();
                }
                ctx.globalAlpha = 1;
            }

            // nodes, far → near
            const drawn = g.nodes
                .map((n) => ({ n, q: proj[n.id] }))
                .filter((x) => x.q)
                .sort((p, q2) => q2.q.depth - p.q.depth);
            const hover = hoverRef.current;
            for (const { n, q } of drawn) {
                const color = NODE_COLORS[n.status] || C.dim;
                const rr = (n.layer === 3 ? 9 : 8) * q.s * Math.max(0.7, zoom);
                const isSel = selected && selected.id === n.id;
                const isHover = hover === n.id;
                const dimOut = selected && !isSel
                    && !(g.edges || []).some((e) =>
                        (e.from === selected.id && e.to === n.id)
                        || (e.to === selected.id && e.from === n.id));
                ctx.globalAlpha = dimOut ? 0.25 : 1;
                // glow
                ctx.shadowColor = color;
                ctx.shadowBlur = n.status === "firing"
                    ? 18 + 8 * Math.sin(tms * 0.01)
                    : (isSel || isHover ? 16 : 8);
                ctx.beginPath();
                ctx.arc(q.x, q.y, rr, 0, Math.PI * 2);
                ctx.fillStyle = "#0E1722";
                ctx.fill();
                ctx.shadowBlur = 0;
                ctx.lineWidth = isSel ? 2.4 : 1.6;
                ctx.strokeStyle = color;
                ctx.stroke();
                // core dot
                ctx.beginPath();
                ctx.arc(q.x, q.y, Math.max(1.5, rr * 0.42), 0, Math.PI * 2);
                ctx.fillStyle = color;
                ctx.fill();
                // broken cross
                if (n.status === "down") {
                    ctx.strokeStyle = C.bear;
                    ctx.lineWidth = 1.4;
                    ctx.beginPath();
                    ctx.moveTo(q.x - rr, q.y - rr); ctx.lineTo(q.x + rr, q.y + rr);
                    ctx.moveTo(q.x + rr, q.y - rr); ctx.lineTo(q.x - rr, q.y + rr);
                    ctx.stroke();
                }
                // label (fade with depth)
                const la = Math.max(0.25, Math.min(1, q.s * 1.1))
                    * (dimOut ? 0.3 : 1);
                ctx.globalAlpha = la;
                ctx.font = `${isSel || isHover ? "bold " : ""}11px Inter, ui-sans-serif`;
                ctx.textAlign = "center";
                ctx.fillStyle = isSel || isHover ? C.text : C.sub;
                ctx.fillText(n.label, q.x, q.y + rr + 12);
                ctx.globalAlpha = 1;
            }
            rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);
        return () => {
            cancelAnimationFrame(rafRef.current);
            ro.disconnect();
            canvas.removeEventListener("wheel", wheel);
        };
    }, [selected, kindFilter]);

    // ---- interaction ----
    const pick = useCallback((clientX, clientY) => {
        const g = graphRef.current;
        const canvas = canvasRef.current;
        const wrap = wrapRef.current;
        if (!g || !canvas || !wrap) return null;
        const rect = canvas.getBoundingClientRect();
        const x = clientX - rect.left, y = clientY - rect.top;
        const { yaw, pitch, zoom } = camRef.current;
        const cx = wrap.clientWidth / 2, cy = wrap.clientHeight / 2 + 10;
        let best = null, bestD = 18 * 18;
        for (const n of g.nodes) {
            const p = posRef.current[n.id];
            if (!p) continue;
            const q = project(p, yaw, pitch, zoom, cx, cy);
            const d = (q.x - x) ** 2 + (q.y - y) ** 2;
            if (d < bestD) { bestD = d; best = n; }
        }
        return best;
    }, []);

    const onPointerDown = (ev) => {
        dragRef.current = { active: true, lastX: ev.clientX, lastY: ev.clientY,
                            lastMove: performance.now(), moved: 0 };
        ev.currentTarget.setPointerCapture?.(ev.pointerId);
    };
    const onPointerMove = (ev) => {
        const d = dragRef.current;
        if (d.active) {
            const dx = ev.clientX - d.lastX, dy = ev.clientY - d.lastY;
            d.moved = (d.moved || 0) + Math.abs(dx) + Math.abs(dy);
            camRef.current.yaw += dx * 0.006;
            camRef.current.pitch = Math.max(
                -1.2, Math.min(1.35, camRef.current.pitch + dy * 0.004));
            d.lastX = ev.clientX; d.lastY = ev.clientY;
            d.lastMove = performance.now();
        } else {
            const n = pick(ev.clientX, ev.clientY);
            hoverRef.current = n ? n.id : null;
            ev.currentTarget.style.cursor = n ? "pointer" : "grab";
        }
    };
    const onPointerUp = (ev) => {
        const d = dragRef.current;
        dragRef.current = { ...d, active: false, lastMove: performance.now() };
        if ((d.moved || 0) < 6) {
            const n = pick(ev.clientX, ev.clientY);
            setSelected(n || null);
        }
    };
    // edges touching the selected node, for the side panel
    const selEdges = useMemo(() => {
        if (!graph || !selected) return { inputs: [], outputs: [] };
        const label = (id) =>
            graph.nodes.find((n) => n.id === id)?.label || id;
        const rows = graph.edges.filter(
            (e) => e.from === selected.id || e.to === selected.id);
        return {
            inputs: rows.filter((e) => e.to === selected.id)
                .map((e) => ({ ...e, other: label(e.from) })),
            outputs: rows.filter((e) => e.from === selected.id)
                .map((e) => ({ ...e, other: label(e.to) })),
        };
    }, [graph, selected]);

    const selNode = useMemo(
        () => (graph && selected
            ? graph.nodes.find((n) => n.id === selected.id) || selected
            : null),
        [graph, selected]);

    const edgeBadge = (e) => {
        const st = e.status;
        const kindColor = e.kind === "positive" ? C.bull
            : e.kind === "negative" ? C.warn : C.blue;
        const stColor = st === "broken" || st === "firing" ? C.bear
            : st === "masked" || st === "off" ? C.dim
            : st === "degraded" ? C.warn : C.bull;
        return (
            <div key={`${e.from}->${e.to}-${e.kind}`}
                 className="flex items-start gap-2 py-1.5"
                 style={{ borderBottom: `1px solid ${C.border}` }}>
                <span className="mt-0.5 w-4 text-center font-mono text-[12px]"
                      style={{ color: kindColor }}>
                    {e.kind === "positive" ? "+" : e.kind === "negative" ? "−" : "·"}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-[12px] truncate" style={{ color: C.text }}>
                        {e.other}
                    </div>
                    <div className="text-[11px]" style={{ color: C.dim }}>
                        {e.label}
                    </div>
                </div>
                <span className="text-[10px] uppercase tracking-wider mt-0.5"
                      style={{ color: stColor }}>{st}</span>
            </div>
        );
    };

    const filters = [
        ["all", "All flows"], ["positive", "+ feedback"],
        ["negative", "− feedback"], ["issues", "Issues"],
    ];

    return (
        <div className="min-h-screen" style={{ background: C.bg, color: C.text }}>
            <div className="sticky top-0 z-40 border-b"
                 style={{ borderColor: C.border,
                          background: "rgba(11,15,20,0.92)",
                          backdropFilter: "blur(8px)" }}>
                <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-14 flex items-center gap-3">
                    <button onClick={onBack}
                            className="btn-anim flex items-center gap-1.5 text-sm"
                            style={{ color: C.sub }}
                            data-testid="system-map-back-button">
                        <ArrowLeft size={15} /> SmartGrid
                    </button>
                    <div className="flex items-center gap-2 ml-2">
                        <Orbit size={16} style={{ color: C.cyan }} />
                        <span className="font-semibold text-sm">System Map</span>
                        <span className="text-[11px]" style={{ color: C.dim }}>
                            live 3D · drag to orbit · wheel to zoom · click a node
                        </span>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        {counts && (
                            <>
                                <StatusChip color={C.bear} label="broken"
                                            count={counts.broken} />
                                <StatusChip color={C.bear} label="firing"
                                            count={counts.firing} />
                                <StatusChip color={C.bull} label="+ loops"
                                            count={counts.positive} />
                                <StatusChip color={C.warn} label="− loops"
                                            count={counts.negative} />
                                {counts.masked > 0 && (
                                    <StatusChip color={C.dim} label="masked"
                                                count={counts.masked} />
                                )}
                            </>
                        )}
                        <button onClick={() => setSpin((s) => !s)}
                                className="btn-anim flex items-center gap-1 px-2 h-7 rounded-md text-[11px]"
                                style={{ border: `1px solid ${C.border}`, color: C.sub }}
                                data-testid="system-map-spin-toggle">
                            {spin ? <Pause size={12} /> : <Play size={12} />}
                            {spin ? "pause" : "spin"}
                        </button>
                        <button onClick={load}
                                className="btn-anim flex items-center gap-1 px-2 h-7 rounded-md text-[11px]"
                                style={{ border: `1px solid ${C.border}`, color: C.sub }}
                                data-testid="system-map-refresh">
                            <RefreshCw size={12} /> refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-3">
                {err && (
                    <div className="mb-2 px-3 py-2 rounded-md text-[12px]"
                         style={{ border: `1px solid ${C.bear}`, color: C.bear }}>
                        system-map fetch failed: {err}
                    </div>
                )}
                <div className="flex items-center gap-2 mb-2">
                    {filters.map(([k, label]) => (
                        <button key={k} onClick={() => setKindFilter(k)}
                                className="btn-anim px-2.5 h-7 rounded-md text-[11px]"
                                style={{
                                    border: `1px solid ${kindFilter === k ? C.cyan : C.border}`,
                                    color: kindFilter === k ? C.cyan : C.sub,
                                }}
                                data-testid={`system-map-filter-${k}`}>
                            {label}
                        </button>
                    ))}
                    <span className="text-[11px] ml-2" style={{ color: C.dim }}>
                        edges: <span style={{ color: C.blue }}>data</span> ·{" "}
                        <span style={{ color: C.bull }}>+ reinforcing</span> ·{" "}
                        <span style={{ color: C.warn }}>− stabilizing</span> ·{" "}
                        <span style={{ color: C.bear }}>broken/firing</span> ·{" "}
                        <span style={{ color: C.dim }}>masked/off</span>
                    </span>
                </div>

                <div className="relative rounded-lg overflow-hidden"
                     style={{ border: `1px solid ${C.border}`,
                              background:
                                "radial-gradient(1200px 600px at 50% 42%, #0E1622 0%, #0B0F14 70%)" }}>
                    <div ref={wrapRef} className="w-full"
                         style={{ height: "calc(100vh - 190px)", minHeight: 480 }}>
                        <canvas ref={canvasRef}
                                data-testid="system-map-canvas"
                                onPointerDown={onPointerDown}
                                onPointerMove={onPointerMove}
                                onPointerUp={onPointerUp}
                                style={{ touchAction: "none", cursor: "grab",
                                         display: "block" }} />
                    </div>

                    {/* node legend */}
                    <div className="absolute left-3 bottom-3 rounded-md px-3 py-2"
                         style={{ background: "rgba(15,22,32,0.92)",
                                  border: `1px solid ${C.border}` }}>
                        <div className="text-[10px] uppercase tracking-[0.16em] mb-1.5"
                             style={{ color: C.dim }}>node health</div>
                        <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                            {Object.entries(NODE_LABELS).map(([k, label]) => (
                                <span key={k}
                                      className="inline-flex items-center gap-1.5 text-[11px]"
                                      style={{ color: C.sub }}>
                                    <span className="w-2 h-2 rounded-full"
                                          style={{ background: NODE_COLORS[k] }} />
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* selected node detail */}
                    {selNode && (
                        <div className="absolute right-3 top-3 w-[320px] max-h-[85%] overflow-auto rounded-md"
                             style={{ background: "rgba(15,22,32,0.95)",
                                      border: `1px solid ${C.border}` }}
                             data-testid="system-map-detail">
                            <div className="px-3 py-2 flex items-center gap-2"
                                 style={{ borderBottom: `1px solid ${C.border}` }}>
                                <span className="w-2.5 h-2.5 rounded-full"
                                      style={{ background:
                                        NODE_COLORS[selNode.status] || C.dim }} />
                                <span className="font-semibold text-[13px]">
                                    {selNode.label}
                                </span>
                                <span className="text-[10px] uppercase tracking-wider ml-1"
                                      style={{ color:
                                        NODE_COLORS[selNode.status] || C.dim }}>
                                    {NODE_LABELS[selNode.status] || selNode.status}
                                </span>
                                <button className="ml-auto text-[12px]"
                                        style={{ color: C.dim }}
                                        onClick={() => setSelected(null)}
                                        data-testid="system-map-detail-close">
                                    ✕
                                </button>
                            </div>
                            <div className="px-3 py-2 text-[12px]"
                                 style={{ color: C.sub }}>
                                {selNode.detail || "—"}
                                {selNode.metrics
                                    && Object.keys(selNode.metrics).length > 0 && (
                                    <div className="mt-1.5 grid grid-cols-2 gap-1">
                                        {Object.entries(selNode.metrics)
                                            .map(([k, v]) => (
                                            <div key={k} className="text-[11px]">
                                                <span style={{ color: C.dim }}>{k}: </span>
                                                <span className="font-mono tabular-nums">
                                                    {typeof v === "number"
                                                        ? +v.toFixed(4) : String(v)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {selEdges.inputs.length > 0 && (
                                <div className="px-3 pb-1">
                                    <div className="text-[10px] uppercase tracking-[0.16em] mt-1 mb-1"
                                         style={{ color: C.dim }}>
                                        inputs ← {selEdges.inputs.length}
                                    </div>
                                    {selEdges.inputs.map(edgeBadge)}
                                </div>
                            )}
                            {selEdges.outputs.length > 0 && (
                                <div className="px-3 pb-2">
                                    <div className="text-[10px] uppercase tracking-[0.16em] mt-2 mb-1"
                                         style={{ color: C.dim }}>
                                        outputs → {selEdges.outputs.length}
                                    </div>
                                    {selEdges.outputs.map(edgeBadge)}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
